import { ReactNode } from 'react';
import { useMultiTeam } from '@/hooks/useMultiTeam';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Lock } from 'lucide-react';

interface MultiTeamGateProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Gate component that only renders children if multi-team is enabled.
 * Shows a locked state for non-Enterprise shops.
 */
export function MultiTeamGate({ children, fallback }: MultiTeamGateProps) {
  const { isMultiTeamEnabled, isLoading, shop } = useMultiTeam();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (isMultiTeamEnabled) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  // Default fallback: show Enterprise upgrade prompt
  const isEnterprise = shop?.tier === 'ENTERPRISE';

  return (
    <Card className="border-dashed">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Lock className="h-6 w-6 text-muted-foreground" />
        </div>
        <CardTitle className="flex items-center justify-center gap-2">
          Multi-Team Support
          <Badge variant="secondary">Enterprise</Badge>
        </CardTitle>
        <CardDescription>
          {isEnterprise
            ? 'Multi-team support is available for your plan. Enable it in Shop Settings to manage multiple service teams.'
            : 'Upgrade to Enterprise to enable multi-team support and manage multiple service teams with independent scheduling.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-center text-sm text-muted-foreground">
        <p>Features include:</p>
        <ul className="mt-2 space-y-1">
          <li>• Independent team scheduling</li>
          <li>• Team-scoped bay assignments</li>
          <li>• Team-based technician management</li>
          <li>• Team performance analytics</li>
        </ul>
      </CardContent>
    </Card>
  );
}
